import { useId, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

const BUTTON_STYLE = {
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '8px 16px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
} as const;

export interface PermissionDeniedProps {
  /** Replaces the generic body text (e.g. "You need the X.y permission…"). */
  detail?: string;
  /** The missing permission, shown as a chip ("Resource.action"). */
  permission?: string;
  /**
   * T-17 D11 — non-navigating inline variant: no "Back to dashboard" button and
   * no router dependency, so a failed panel/page can show the same look.
   */
  inline?: boolean;
  /** Retry button (inline use; permissions can change). */
  onRetry?: () => void;
}

interface CardProps extends PermissionDeniedProps {
  /** Present → renders the "Back to dashboard" button. */
  onBack?: () => void;
  /** False when a wrapper (ApiErrorNotice) carries the role="alert". */
  asAlert: boolean;
}

/** The one visual for "you may not do this" (shared by the route guard and ApiErrorNotice). */
function PermissionDeniedCard({ detail, permission, onBack, onRetry, asAlert }: CardProps): ReactElement {
  const headingId = useId();
  return (
    <section
      {...(asAlert && { role: 'alert' })}
      aria-labelledby={headingId}
      style={{
        maxWidth: 560,
        margin: '40px auto',
        padding: 24,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        textAlign: 'center',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          fontSize: 40,
          marginBottom: 12,
          color: 'var(--red)',
        }}
      >
        🔒
      </div>
      <h2 id={headingId} style={{ margin: '0 0 12px 0', color: 'var(--accent)' }}>
        Access denied
      </h2>
      <p style={{ margin: '0 0 20px 0', color: 'var(--text-muted)', fontSize: 14 }}>
        {detail ?? (
          <>
            You don&apos;t have permission to access this page. If you think this is a mistake,
            contact an administrator.
          </>
        )}
      </p>
      {permission && (
        <p style={{ margin: '0 0 20px 0' }}>
          <code data-testid="permission-denied-permission">{permission}</code>
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        {onBack && (
          <button type="button" onClick={onBack} style={BUTTON_STYLE}>
            ← Back to dashboard
          </button>
        )}
        {onRetry && (
          <button type="button" data-testid="api-error-retry" onClick={onRetry} style={BUTTON_STYLE}>
            Retry
          </button>
        )}
      </div>
    </section>
  );
}

function NavigatingPermissionDenied(props: PermissionDeniedProps): ReactElement {
  const navigate = useNavigate();
  return <PermissionDeniedCard {...props} asAlert onBack={() => navigate('/home')} />;
}

export function PermissionDenied(props: PermissionDeniedProps = {}): ReactElement {
  if (props.inline) return <PermissionDeniedCard {...props} asAlert />;
  return <NavigatingPermissionDenied {...props} />;
}

/** Inline card without its own alert role — for wrappers (ApiErrorNotice) that carry role="alert". */
export function PermissionDeniedBody(props: PermissionDeniedProps): ReactElement {
  return <PermissionDeniedCard {...props} asAlert={false} />;
}
